import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { attachDetailBehavior } from '@viet-parenting/content/detail';
import type { Locale } from '@viet-parenting/content/i18n';
import { previewContext } from '../lib/preview';
import type { DetailContext } from '@viet-parenting/content/detail';

interface Props {
  onClose: () => void;
  /** Renders the public detail markup for whichever locale is selected. */
  children: (ctx: DetailContext) => ReactNode;
}

const LOCALES: ReadonlyArray<{ value: Locale; label: string }> = [
  { value: 'vi', label: 'Tiếng Việt' },
  { value: 'en', label: 'English' },
];

/**
 * Shows a draft exactly as the public site renders it, without publishing it.
 *
 * The markup comes from the same components apps/web builds its detail pages
 * from, so what an admin sees here is what readers will see once the row is
 * approved — minus the site header and footer, which the modal replaces with
 * its own toolbar.
 */
export default function PreviewModal({ onClose, children }: Props) {
  const [locale, setLocale] = useState<Locale>('vi');
  const surfaceRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  /* Carousel and sticky-bar behavior, the same module the public pages run. */
  useEffect(() => {
    if (!surfaceRef.current) return;
    return attachDetailBehavior(surfaceRef.current);
  }, [locale]);

  useEffect(() => {
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  /* Links inside a preview point at public routes that do not exist here, so
     none of them may navigate away from the form the admin is editing. */
  const swallowNavigation = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('a')) e.preventDefault();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-gray-900/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Xem trước nội dung"
        className="flex flex-col flex-1 min-h-0 m-0 sm:m-4 lg:m-6 bg-white rounded-none sm:rounded-2xl overflow-hidden shadow-2xl"
      >
        <header className="flex items-center gap-3 flex-wrap px-4 sm:px-6 py-3 border-b border-gray-200 bg-white">
          <span className="font-display font-extrabold text-red-700">Xem trước</span>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">
            Bản nháp — chưa xuất bản
          </span>

          <div className="ml-auto flex items-center gap-1 rounded-full border border-gray-200 p-0.5">
            {LOCALES.map((l) => (
              <button
                key={l.value}
                type="button"
                onClick={() => setLocale(l.value)}
                aria-pressed={locale === l.value}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                  locale === l.value ? 'bg-red-50 text-red-700' : 'text-gray-500 hover:text-red-700'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="border border-gray-200 text-gray-700 text-sm font-medium px-4 py-1.5 rounded-lg hover:border-red-300 transition-colors"
          >
            Đóng
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto vp-preview-surface">
          <div ref={surfaceRef} onClick={swallowNavigation}>
            {children(previewContext(locale))}
          </div>
        </div>
      </div>
    </div>
  );
}
